import {
  HelmetDexteel,
  TimeAndUserMenu,
  useUTLSettingsContext,
} from "@dexteel/mesf-core";
import { Box, Button, Paper, TextField, Typography } from "@mui/material";
import {
  Person,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { get } from "lodash-es";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Charge, ChargeStatusCode } from "@/pages/sy/models/Charge";
import { useChargesQuery } from "@/pages/sy/hooks/queries/useChargesQuery";
import {
  useDeleteChargeMutation,
  useOpenChargeMutation,
  useUpsertChargeMutation,
  useUpsertHeatWeightRequestMutation,
} from "@/pages/sy/hooks/queries/useChargeMutations";
import { useOperatorsQuery } from "@/pages/sy/hooks/queries/useOperatorsQuery";

import { useTheme } from "@mui/material/styles";
import { chargeSelectionStyles } from "@/pages/sy/styles/ChargeSelectionStyles";
import { NewChargePanel } from "./components/NewChargePanel";
import { OperatorSelector } from "./components/OperatorSelector";
import { TableChargesSelection } from "./components/TableChargesSelection";
import { TableScheduledHeats } from "./components/TableScheduledHeats";
import { ErrorDetailsModal, ErrorDetail } from "./modals/ErrorDetailsModal";
import { WarningModal } from "./modals/WarningModal";
import { RequestedHeatWeightModal } from "./modals/RequestedHeatWeightModal";
import { calculateHeatWeightScaling } from "./utils/heatWeightScaling";
import { formatCraneMagnitude } from "./utils/formatCraneMagnitude";

interface LocationState {
  previousOperatorId?: string | number;
  previousChargeId?: number;
  previousScheduledHeatName?: string;
}

export interface HeatGroup {
  scheduledHeatName: string;
  recipeName: string;
  charges: Charge[];
}

const MAX_HEATS_PER_PAGE = 5;

export const ChargeSelectionScreen = () => {
  const theme = useTheme();
  const styles = useMemo(() => chargeSelectionStyles(theme), [theme]);
  const navigate = useNavigate();
  const location = useLocation();
  const [operatorId, setOperatorId] = useState<number | null>(() => {
    try {
      const storedOperatorId = localStorage.getItem("selectedOperatorId");
      return storedOperatorId ? parseInt(storedOperatorId) : null;
    } catch (error) {
      console.error("Failed to read operatorId from localStorage", error);
      return null;
    }
  });
  const queryClient = useQueryClient();
  const openChargeMutation = useOpenChargeMutation();
  const upsertChargeMutation = useUpsertChargeMutation();
  const deleteChargeMutation = useDeleteChargeMutation();
  const upsertHeatWeightRequestMutation = useUpsertHeatWeightRequestMutation();
  const { data: operators = [] } = useOperatorsQuery();

  const { state: utlState } = useUTLSettingsContext();
  const refreshTime = parseInt(
    get(utlState.settings, "SCRAP_CRANE_APP_REFRESH_TIME", 1),
  );

  const { data: charges = [] } = useChargesQuery(refreshTime);

  const currentHeatName = charges[0]?.CurrentHeatName ?? null;
  const currentHeatUnlinked = charges[0]?.CurrentHeatUnlinked ?? null;

  const [selectedChargeId, setSelectedChargeId] = useState<number | null>(null);
  const selectedCharge = useMemo(
    () => charges.find((c) => c.ChargeId === selectedChargeId) ?? null,
    [charges, selectedChargeId],
  );
  const [selectedHeatName, setSelectedHeatName] = useState<string | null>(null);
  const [heatVisibleRange, setHeatVisibleRange] = useState({
    start: 0,
    end: MAX_HEATS_PER_PAGE,
  });
  const [showNewChargePanel, setShowNewChargePanel] = useState(false);
  const restoredNavigationSelection = useRef(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogData, setErrorDialogData] = useState<{
    title: string;
    message: string;
    details?: ErrorDetail[];
    detailsTitle?: string;
    guidanceMessage?: string;
    isBucketConflict?: boolean;
  } | null>(null);
  const [conflictingCharge, setConflictingCharge] = useState<Charge | null>(
    null,
  );
  const [isEAFConfirmModalOpen, setIsEAFConfirmModalOpen] = useState(false);
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] =
    useState(false);
  const [isRequestedWeightModalOpen, setIsRequestedWeightModalOpen] =
    useState(false);
  const requestedHeatWeightInputRef = useRef<HTMLInputElement>(null);

  const setError = (message: string) => {
    if (message) {
      setErrorDialogData({
        title: "Error",
        message: message,
      });
      setErrorDialogOpen(true);
    }
  };

  // Group charges by Scheduled Heat only
  const heatGroups = useMemo((): HeatGroup[] => {
    const groupMap = new Map<string, HeatGroup>();

    charges.forEach((charge) => {
      const heatName = charge.ScheduledHeatName || "No Heat";
      const recipeName = charge.RecipeName || "-";
      const key = heatName; // Group only by Scheduled Heat

      if (!groupMap.has(key)) {
        groupMap.set(key, {
          scheduledHeatName: heatName,
          recipeName: recipeName,
          charges: [],
        });
      }

      groupMap.get(key)!.charges.push(charge);
    });

    return Array.from(groupMap.values());
  }, [charges]);

  const visibleHeatGroups = useMemo(() => {
    return heatGroups.slice(heatVisibleRange.start, heatVisibleRange.end);
  }, [heatGroups, heatVisibleRange]);

  const selectedHeatIndex = useMemo(() => {
    if (selectedHeatName === null) return null;

    const index = visibleHeatGroups.findIndex(
      (heatGroup) => heatGroup.scheduledHeatName === selectedHeatName,
    );
    return index === -1 ? null : index;
  }, [selectedHeatName, visibleHeatGroups]);

  const selectedHeatGroup = useMemo(() => {
    if (selectedHeatName === null) return null;

    return (
      heatGroups.find(
        (heatGroup) => heatGroup.scheduledHeatName === selectedHeatName,
      ) ?? null
    );
  }, [heatGroups, selectedHeatName]);

  const requestedHeatWeight = useMemo(() => {
    const value = selectedHeatGroup?.charges.find(
      (charge) => charge.RequestedHeatWeight != null,
    )?.RequestedHeatWeight;

    return value == null ? null : Number(value);
  }, [selectedHeatGroup]);

  const recipeTotalWeight = useMemo(
    () =>
      selectedHeatGroup?.charges.reduce(
        (total, charge) => total + Number(charge.ManualTargetWeight ?? 0),
        0,
      ) ?? 0,
    [selectedHeatGroup],
  );

  const actualHeatWeight = useMemo(
    () =>
      selectedHeatGroup?.charges.reduce(
        (total, charge) => total + Number(charge.TotalWeight ?? 0),
        0,
      ) ?? 0,
    [selectedHeatGroup],
  );

  const actualScrapWeight = useMemo(
    () =>
      selectedHeatGroup?.charges.reduce(
        (total, charge) =>
          total +
          Number(charge.TotalWeight ?? 0) -
          Number(charge.FluxWeight ?? 0),
        0,
      ) ?? 0,
    [selectedHeatGroup],
  );

  const heatWeightScaling = useMemo(
    () => calculateHeatWeightScaling(selectedHeatGroup?.charges ?? []),
    [selectedHeatGroup],
  );

  const formattedRequestedHeatWeight = useMemo(
    () => formatCraneMagnitude(requestedHeatWeight, ""),
    [requestedHeatWeight],
  );

  useEffect(() => {
    if (location.state) {
      const state = location.state as LocationState;
      if (state.previousOperatorId) {
        const storedOperatorIdFromNav =
          typeof state.previousOperatorId === "string"
            ? parseInt(state.previousOperatorId)
            : state.previousOperatorId;

        if (
          !isNaN(storedOperatorIdFromNav) &&
          storedOperatorIdFromNav !== operatorId
        ) {
          setOperatorId(storedOperatorIdFromNav);
          try {
            localStorage.setItem(
              "selectedOperatorId",
              storedOperatorIdFromNav.toString(),
            );
          } catch (error) {
            console.error("Failed to save operatorId to localStorage", error);
          }
        }
      }
    }
  }, [location.state]);

  useEffect(() => {
    if (restoredNavigationSelection.current || charges.length === 0) return;

    const state = location.state as LocationState | null;
    if (state?.previousChargeId == null) return;

    const previousCharge = charges.find(
      (charge) => charge.ChargeId === state.previousChargeId,
    );
    const scheduledHeatName =
      previousCharge?.ScheduledHeatName ?? state.previousScheduledHeatName;

    if (!previousCharge || !scheduledHeatName) return;

    const heatIndex = heatGroups.findIndex(
      (heatGroup) => heatGroup.scheduledHeatName === scheduledHeatName,
    );

    if (heatIndex === -1) return;

    const pageStart =
      Math.floor(heatIndex / MAX_HEATS_PER_PAGE) * MAX_HEATS_PER_PAGE;
    setHeatVisibleRange({
      start: pageStart,
      end: Math.min(pageStart + MAX_HEATS_PER_PAGE, heatGroups.length),
    });
    setSelectedHeatName(scheduledHeatName);
    setSelectedChargeId(previousCharge.ChargeId ?? null);
    restoredNavigationSelection.current = true;
  }, [charges, heatGroups, location.state]);

  // Keep the selected heat selected even when recent activity changes its order.
  useEffect(() => {
    if (selectedHeatName === null) return;

    const heatIndex = heatGroups.findIndex(
      (heatGroup) => heatGroup.scheduledHeatName === selectedHeatName,
    );

    if (heatIndex === -1) {
      setSelectedHeatName(null);
      setSelectedChargeId(null);
      return;
    }

    const pageStart =
      Math.floor(heatIndex / MAX_HEATS_PER_PAGE) * MAX_HEATS_PER_PAGE;
    if (pageStart !== heatVisibleRange.start) {
      setHeatVisibleRange({
        start: pageStart,
        end: Math.min(pageStart + MAX_HEATS_PER_PAGE, heatGroups.length),
      });
    }
  }, [heatGroups, heatVisibleRange.start, selectedHeatName]);

  const handleChargeSelect = (charge: Charge) => {
    setSelectedChargeId(charge.ChargeId ?? null);
  };

  const handleHeatSelect = (index: number) => {
    setSelectedHeatName(visibleHeatGroups[index]?.scheduledHeatName ?? null);
    setSelectedChargeId(null);
  };

  const totalPages = Math.ceil(heatGroups.length / MAX_HEATS_PER_PAGE);
  const currentPage =
    Math.floor(heatVisibleRange.start / MAX_HEATS_PER_PAGE) + 1;

  const handleHeatNavigateLeft = () => {
    if (currentPage > 1) {
      const newPage = currentPage - 1;
      const newStart = (newPage - 1) * MAX_HEATS_PER_PAGE;
      const newEnd = Math.min(newStart + MAX_HEATS_PER_PAGE, heatGroups.length);

      setHeatVisibleRange({
        start: newStart,
        end: newEnd,
      });

      // Clear selection when changing pages
      setSelectedHeatName(null);
      setSelectedChargeId(null);
    }
  };

  const handleHeatNavigateRight = () => {
    if (currentPage < totalPages) {
      const newPage = currentPage + 1;
      const newStart = (newPage - 1) * MAX_HEATS_PER_PAGE;
      const newEnd = Math.min(newStart + MAX_HEATS_PER_PAGE, heatGroups.length);

      setHeatVisibleRange({
        start: newStart,
        end: newEnd,
      });

      // Clear selection when changing pages
      setSelectedHeatName(null);
      setSelectedChargeId(null);
    }
  };

  const handleActionClick = (action: "start" | "continue" | "reopen") => {
    if (!selectedCharge) return;

    // Check if bucket is already in use by another open charge
    if (action === "start" || action === "reopen") {
      const bucketInUse = charges.find(
        (c: Charge) =>
          c.ChargeId !== selectedCharge.ChargeId &&
          c.BucketId === selectedCharge.BucketId &&
          (c.ChargeStatusCode === "O" || c.ChargeStatusCode === "C"), // Only check open charges
      );

      if (bucketInUse) {
        setConflictingCharge(bucketInUse);
        setErrorDialogData({
          title: "Bucket Conflict Error",
          message: `Cannot open charge. Bucket ${selectedCharge.BucketName || selectedCharge.BucketId} is already in use by another open / closed charge.`,
          details: [
            { label: "Conflicting Charge ID", value: bucketInUse.ChargeId },
            {
              label: "Heat Name",
              value: bucketInUse.ScheduledHeatName || "N/A",
            },
            { label: "Charge Number", value: bucketInUse.ChargeNumber },
            { label: "Status", value: "Open" },
          ],
          detailsTitle: "Conflicting Charge Details:",
          guidanceMessage:
            "Please choose an option below to resolve the conflict.",
          isBucketConflict: true,
        });
        setErrorDialogOpen(true);
        return;
      }

      openChargeMutation.mutate(selectedCharge.ChargeId as number, {
        onSuccess: () => {
          navigate(
            `/scrap-charges/operation/${selectedCharge.ChargeId}/${action}/${
              selectedCharge.ChargeGroupNumber
            }?operatorId=${operatorId || ""}`,
            {
              state: {
                chargeNumber: selectedCharge.ChargeNumber || 1,
              },
            },
          );
        },
        onError: (error) => {
          console.error("Error opening charge:", error);
          setErrorDialogData({
            title: "Operation Error",
            message:
              error instanceof Error
                ? error.message
                : "Error opening charge: " + error,
          });
          setErrorDialogOpen(true);
        },
      });
      return;
    }

    // For "continue" action, no need to open charge, just navigate
    navigate(
      `/scrap-charges/operation/${selectedCharge.ChargeId}/${action}/${
        selectedCharge.ChargeGroupNumber
      }?operatorId=${operatorId || ""}`,
      {
        state: {
          chargeNumber: selectedCharge.ChargeNumber || 1,
        },
      },
    );
  };

  const handleErrorDialogClose = () => {
    setErrorDialogOpen(false);
    setErrorDialogData(null);
    setConflictingCharge(null);
  };

  const handleContinueWithConflictingCharge = () => {
    if (!conflictingCharge) return;

    handleErrorDialogClose();
    navigate(
      `/scrap-charges/operation/${conflictingCharge.ChargeId}/continue/${
        conflictingCharge.ChargeGroupNumber
      }?operatorId=${operatorId || ""}`,
      {
        state: {
          chargeNumber: conflictingCharge.ChargeNumber || 1,
        },
      },
    );
  };

  const handleContinueWithSelectedCharge = async () => {
    if (!conflictingCharge || !selectedCharge) return;

    try {
      // Update conflicting charge status to "H" (Charged Into EAF)
      await upsertChargeMutation.mutateAsync({
        ...conflictingCharge,
        ChargeStatusCode: "H",
        OperatorId: operatorId,
      });

      // Open the selected charge
      await openChargeMutation.mutateAsync(selectedCharge.ChargeId as number);

      handleErrorDialogClose();

      // Navigate to selected charge operation screen
      navigate(
        `/scrap-charges/operation/${selectedCharge.ChargeId}/start/${
          selectedCharge.ChargeGroupNumber
        }?operatorId=${operatorId || ""}`,
        {
          state: {
            chargeNumber: selectedCharge.ChargeNumber || 1,
          },
        },
      );
    } catch (err) {
      console.error("Error handling charge conflict:", err);
      setError(
        err instanceof Error
          ? err.message
          : "Error handling charge conflict: " + err,
      );
    }
  };

  type ActionableStatus = Exclude<ChargeStatusCode, "H">;

  const STATUS_ACTION_CONFIG = {
    S: { label: "START", action: "start" },
    O: { label: "CONTINUE CHARGE", action: "continue" },
    C: { label: "REOPEN", action: "reopen" },
  } as const satisfies Record<
    ActionableStatus,
    { label: string; action: string }
  >;

  type StatusActionKey = keyof typeof STATUS_ACTION_CONFIG;

  const isStatusActionKey = (key: string): key is StatusActionKey =>
    key in STATUS_ACTION_CONFIG;

  const statusAction =
    selectedCharge && isStatusActionKey(selectedCharge.ChargeStatusCode)
      ? STATUS_ACTION_CONFIG[selectedCharge.ChargeStatusCode]
      : null;

  const canChargeIntoEAF =
    selectedCharge !== null &&
    selectedCharge.ChargeStatusCode !== "H" &&
    selectedCharge.ChargeStatusCode !== "S" &&
    (selectedCharge.TotalWeight ?? 0) > 0;

  const canDeleteCharge =
    selectedCharge !== null && (selectedCharge.TotalWeight ?? 0) === 0;

  const handleDeleteCharge = () => {
    if (!selectedCharge?.ChargeId) return;

    deleteChargeMutation.mutate(selectedCharge.ChargeId as number, {
      onSuccess: () => {
        setSelectedChargeId(null);
        setIsDeleteConfirmModalOpen(false);
      },
      onError: (error) => {
        setIsDeleteConfirmModalOpen(false);
        setError(
          "Error deleting charge: " +
            (error instanceof Error ? error.message : String(error)),
        );
      },
    });
  };

  const handleChargeIntoEAF = () => {
    if (!selectedCharge?.ChargeId) return;

    upsertChargeMutation.mutate(
      {
        ...selectedCharge,
        ChargeStatusCode: "H",
        OperatorId: operatorId,
      },
      {
        onSuccess: () => {
          setSelectedChargeId(null);
        },
        onError: (error) => {
          setError(
            "Error charging into EAF: " +
              (error instanceof Error ? error.message : String(error)),
          );
        },
      },
    );
  };

  const handleSchedule = () => {
    setShowNewChargePanel(false);
    // No need to invalidate manually - NewChargePanel uses mutations that auto-invalidate
  };

  const handleNewCharge = async () => {
    // Refresh recipes to get latest data (e.g., after setting a recipe as current)
    // recipes() without params returns prefix that invalidates all variants
    await queryClient.invalidateQueries({
      queryKey: ["recipes"],
    });
    setShowNewChargePanel(true);
  };

  const handleSaveRequestedHeatWeight = async (value: number | null) => {
    if (!selectedHeatGroup) return;

    try {
      await upsertHeatWeightRequestMutation.mutateAsync({
        scheduledHeatName: selectedHeatGroup.scheduledHeatName,
        requestedHeatWeight: value,
      });
      setIsRequestedWeightModalOpen(false);
    } catch (error) {
      setIsRequestedWeightModalOpen(false);
      setError(
        "Error saving Requested Heat Weight: " +
          (error instanceof Error ? error.message : String(error)),
      );
    }
  };

  const handleOperatorChange = (newOperatorId: number) => {
    setOperatorId(newOperatorId);
    try {
      localStorage.setItem("selectedOperatorId", newOperatorId.toString());
    } catch (error) {
      console.error("Failed to save operatorId to localStorage", error);
    }
  };

  const [operatorDialogOpen, setOperatorDialogOpen] = useState(false);
  const selectedOperator = operators.find((op) => op.OperatorId === operatorId);

  const isStatusActionEnabled = statusAction !== null;

  useEffect(() => {
    if (!operatorId) {
      setOperatorDialogOpen(true);
    }
  }, [operatorId]);

  return (
    <Box sx={styles.root}>
      <HelmetDexteel title="Charge Selection" />
      <Box sx={styles.header}>
        <div
          style={{
            width: "33%",
            padding: "0 16px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <Button
            component={Link}
            to="/meltshop-dashboard"
            style={{
              color: "white",
              fontSize: "0.875rem",
              fontWeight: 500,
              textTransform: "uppercase",
            }}
          >
            Dashboard
          </Button>
          {currentHeatName ? (
            <Typography
              variant="h6"
              style={{ fontWeight: 600, marginLeft: "16px" }}
            >
              {!!currentHeatUnlinked
                ? `EAF Heat: Unlinked (Linking in Progress for ${currentHeatName})`
                : `EAF Heat: ${currentHeatName}`}
            </Typography>
          ) : null}
        </div>
        <div
          style={{
            width: "34%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography variant="h4" sx={styles.headerTitle}>
            Charge Selection
          </Typography>
        </div>
        <div
          style={{
            width: "33%",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "20px",
          }}
        >
          <Button
            style={{
              textTransform: "none",
              color: "white",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={() => setOperatorDialogOpen(true)}
          >
            <Person style={{ fontSize: "2rem", marginRight: "8px" }} />
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              {selectedOperator?.OperatorName || "Select Operator"}
            </Typography>
          </Button>
          <TimeAndUserMenu />
        </div>
      </Box>

      <OperatorSelector
        selectedOperatorId={operatorId}
        onChange={handleOperatorChange}
        operators={operators}
        open={operatorDialogOpen}
        onClose={() => setOperatorDialogOpen(false)}
      />

      <Box sx={styles.mainContent}>
        {/* Left Section: Scheduled Heat Table */}
        <Box sx={styles.heatListSection}>
          <Paper sx={styles.heatTableCard} elevation={4}>
            <TableScheduledHeats
              visibleHeatGroups={visibleHeatGroups}
              selectedHeatIndex={selectedHeatIndex}
              handleHeatSelect={handleHeatSelect}
            />

            <Box sx={styles.heatNavigationContainer}>
              {/* Previous Button */}
              <Button
                variant="contained"
                color="primary"
                sx={styles.heatNavPrevButton}
                onClick={handleHeatNavigateLeft}
                disabled={currentPage === 1}
              >
                <ChevronLeftIcon />
              </Button>

              {/* Page Number */}
              <Box sx={styles.heatNavPageNumber}>{currentPage}</Box>

              {/* Next Button */}
              <Button
                variant="contained"
                color="primary"
                sx={styles.heatNavNextButton}
                onClick={handleHeatNavigateRight}
                disabled={currentPage === totalPages}
              >
                <ChevronRightIcon />
              </Button>
            </Box>
          </Paper>
        </Box>

        {/* Middle Section: Charges Table */}
        <Box sx={styles.chargesSection}>
          <Paper sx={styles.chargesTableCard} elevation={4}>
            <TableChargesSelection
              selectedCharge={selectedCharge}
              selectedHeatGroup={selectedHeatGroup}
              handleChargeSelect={handleChargeSelect}
              scaledTargetsByChargeId={heatWeightScaling?.targetsByChargeId}
              actualScrapWeight={actualScrapWeight}
              actualTotalWeight={actualHeatWeight}
              recipeTotalWeight={recipeTotalWeight}
              requestedHeatWeight={requestedHeatWeight}
            />
            <Box sx={styles.requestedWeightFooter}>
              <Typography sx={styles.requestedWeightLabel}>
                Requested Heat Weight
              </Typography>
              <TextField
                inputRef={requestedHeatWeightInputRef}
                value={formattedRequestedHeatWeight}
                placeholder={selectedHeatGroup ? "Tap to set" : ""}
                disabled={!selectedHeatGroup}
                onClick={() => {
                  requestedHeatWeightInputRef.current?.blur();
                  if (selectedHeatGroup) setIsRequestedWeightModalOpen(true);
                }}
                sx={styles.requestedWeightInput}
                slotProps={{
                  htmlInput: {
                    readOnly: true,
                    inputMode: "none",
                    "aria-label": "Requested Heat Weight",
                  },
                }}
              />
            </Box>
          </Paper>
        </Box>

        {/* Right Section: Action Buttons */}
        <Box sx={styles.actionSection}>
          <Paper sx={styles.actionCard} elevation={4}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              sx={{
                ...(styles.actionButton as object),
                ...(!isStatusActionEnabled
                  ? (styles.disabledButton as object)
                  : {}),
              }}
              disabled={!isStatusActionEnabled}
              onClick={() =>
                statusAction && handleActionClick(statusAction.action)
              }
            >
              {statusAction?.label ?? "SELECT A CHARGE"}
            </Button>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {canDeleteCharge ? (
                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    ...(styles.deleteButton as object),
                  }}
                  onClick={() => setIsDeleteConfirmModalOpen(true)}
                >
                  DELETE CHARGE
                </Button>
              ) : (
                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    ...(styles.eafButton as object),
                    ...(!canChargeIntoEAF
                      ? (styles.disabledButton as object)
                      : {}),
                  }}
                  disabled={!canChargeIntoEAF}
                  onClick={() => setIsEAFConfirmModalOpen(true)}
                >
                  CHARGED INTO EAF
                </Button>
              )}
              <Button
                variant="contained"
                fullWidth
                sx={styles.newChargeButton}
                onClick={handleNewCharge}
                disabled={!operatorId}
              >
                NEW CHARGE
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>

      {showNewChargePanel ? (
        <Paper sx={styles.newChargePanel} elevation={6}>
          <NewChargePanel
            open={showNewChargePanel}
            onSchedule={handleSchedule}
            onCancel={() => setShowNewChargePanel(false)}
            operatorId={operatorId}
            chargeGroupNumber={
              selectedHeatGroup?.charges[0]?.ChargeGroupNumber ?? null
            }
            scheduledHeatName={
              selectedHeatGroup?.charges[0]?.ScheduledHeatName ||
              currentHeatName
            }
            currentHeatName={currentHeatName}
            presetChargeNumber={undefined}
            disableHeatNameEdit={false}
            charges={charges}
          />
        </Paper>
      ) : null}

      <WarningModal
        open={isEAFConfirmModalOpen}
        title="Charge Into EAF or Not Used"
        message="The charge will be permanently marked as charged into EAF (or Not Used) and removed from the active queue. This cannot be undone."
        guidanceMessage="Are you sure you want to proceed?"
        onCancel={() => setIsEAFConfirmModalOpen(false)}
        onProceed={() => {
          handleChargeIntoEAF();
          setIsEAFConfirmModalOpen(false);
        }}
        cancelText="CANCEL"
        proceedText="CONFIRM"
        proceedColor="secondary"
      />

      <WarningModal
        open={isDeleteConfirmModalOpen}
        title="Delete Charge"
        message={`Are you sure you want to delete charge ${selectedCharge?.ScheduledHeatName ?? ""} - ${selectedCharge?.BucketName ?? ""}? This action cannot be undone.`}
        guidanceMessage="The charge will be permanently removed."
        onCancel={() => setIsDeleteConfirmModalOpen(false)}
        onProceed={handleDeleteCharge}
        cancelText="CANCEL"
        proceedText="DELETE"
        proceedColor="secondary"
      />

      <RequestedHeatWeightModal
        open={isRequestedWeightModalOpen}
        requestedHeatWeight={requestedHeatWeight}
        recipeTotalWeight={recipeTotalWeight}
        scheduledHeatName={selectedHeatGroup?.scheduledHeatName ?? null}
        isSaving={upsertHeatWeightRequestMutation.isPending}
        onCancel={() => setIsRequestedWeightModalOpen(false)}
        onSave={handleSaveRequestedHeatWeight}
      />

      {errorDialogData ? (
        <ErrorDetailsModal
          open={errorDialogOpen}
          title={errorDialogData.title}
          message={errorDialogData.message}
          details={errorDialogData.details}
          detailsTitle={errorDialogData.detailsTitle}
          guidanceMessage={errorDialogData.guidanceMessage}
          onClose={handleErrorDialogClose}
          isBucketConflict={errorDialogData.isBucketConflict}
          conflictingCharge={conflictingCharge}
          selectedCharge={selectedCharge}
          onContinueWithConflictingCharge={handleContinueWithConflictingCharge}
          onContinueWithSelectedCharge={handleContinueWithSelectedCharge}
        />
      ) : null}
    </Box>
  );
};
